import {
  DefaultLinkTrackerAttachmentId,
  EventLinkTracker,
  Event,
  LinkMapping,
  TicketV2,
  NotificationType,
} from "@markit/common.types";
import FlatList from "flatlist-react/lib";
import { Colors } from "../../utils/colors";
import {
  isExternalLinkNoData,
  isProductSpecificType,
  isProductWithCheckout,
  productsWithConversions,
} from "@markit/common.utils";
import BlackHoverButton from "../Buttons/BlackHoverButton";
import { Icon } from "@iconify/react";
import { useTheme } from "../../hooks/useTheme";
import React, { useCallback, useMemo, useState } from "react";
import { API } from "../../API";
import { useDispatch } from "react-redux";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import EmptyStateButton from "../../components/Buttons/EmptyStateButton";
import GridTableHeader from "../Headers/GridTableHeader";
import SearchBoxContainer from "../Containers/SearchBoxContainer";
import filter from "lodash.filter";
import AlertContainer from "../Containers/AlertContainer";
import ConfirmDeleteModal from "../Containers/ConfirmPopups/ConfirmDeleteModal";
import { TrackingLinkItem } from "../DisplayItem/TrackingLinkItem";
import EventDashboardLinkAnalyticsBox from "./EventDashboardLinkAnalyticsBox";
import { EmptyStateFlatlist } from "../EmptyStates/EmptyStateFlatlist";
import { TrackingLinkData } from "../../hooks/useLoadTrackingLinksList";
import { CircularProgress } from "@mui/material";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { TicketAnalyticsSection } from "./TicketAnalyticsSection";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import {
  getTotalRevenueForEvent,
  getTotalTipsForEvent,
} from "../../utils/eventUtils/eventUtils";

type AnalyticsPanelProps = {
  event: Event;
  totalGuests: number;
  totalLinkViews: number;
  canMassText: boolean;
  fetchedLinkTrackers: TrackingLinkData[];
  fetchMoreTrackingLinks: () => void;
  isLoadingTrackingLinks: boolean;
  isFinishedTrackingLinks: boolean;
  loadSearchResultsThrottled: (searchTerm: string) => void;
  setTrackingLinkModalVisible: (isTrackingLinkModalVisible: boolean) => void;
  numTotalLinkTrackers: number;
  linkTrackerId: string | undefined;
  setLinkTrackerId: (linkTrackerId: string | undefined) => void;
  setTrackingLinkModalText: (trackingLinkModalText: string) => void;
  ticketAnalyticsData?: {
    userTicket: TicketV2 | undefined;
    totalScanned: number;
    dashboardVisibility: number;
    isPaymentCollectorRole: boolean;
  };
};

export const AnalyticsPanel = (props: AnalyticsPanelProps) => {
  const {
    event,
    totalGuests,
    totalLinkViews,
    canMassText,
    fetchedLinkTrackers,
    isLoadingTrackingLinks,
    isFinishedTrackingLinks,
    fetchMoreTrackingLinks,
    loadSearchResultsThrottled,
    setTrackingLinkModalVisible,
    numTotalLinkTrackers,
    linkTrackerId,
    setLinkTrackerId,
    setTrackingLinkModalText,
    ticketAnalyticsData,
  } = props;
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [alertText, setAlertText] = useState("");
  const [deleteLinkConfirm, setDeleteLinkConfirm] = useState(false);
  const [totalRevenue, setTotalRevenue] = useState<number | null>(null);
  const [totalTips, setTotalTips] = useState<number | null>(null);

  const styles = {
    analyticsContent: {
      gap: 24,
      color: Colors.BLACK,
    },

    analyticsText: {
      fontSize: 32,
    },

    containerView: {
      paddingInline: 14,
    },

    contentView: {
      paddingBottom: 14,
    },

    headerTitle: {
      paddingTop: 14,
      fontSize: 16,
      fontWeight: 600,
    },

    overallContainerView: {
      width: "100%",
      backgroundColor: theme.SecondaryBG.backgroundColor,
      borderRadius: 20,
    },

    sectionHeaderText: {
      marginBottom: 14,
      fontSize: 16,
      fontWeight: 600,
      whiteSpace: "nowrap",
    },

    subText: {
      color: theme.SubText.color,
      fontSize: 12,
    },

    trackingLinkView: {
      marginTop: 24,
      maxHeight: 770,
    },
  };

  useAsyncOnMount(async () => {
    const totalRevenue = await getTotalRevenueForEvent(event.id);
    setTotalRevenue(totalRevenue / 100);
    const totalTips = await getTotalTipsForEvent(event.id);
    setTotalTips(totalTips / 100);
  });

  const showConversions = useMemo(
    () => productsWithConversions(event.eventType, event.accessLevel),
    [event.eventType, event.accessLevel]
  );

  const contains = (trackingLinkData: TrackingLinkData, query: string) => {
    return trackingLinkData.eventLinkTracker.alias
      .toLowerCase()
      .includes(query);
  };

  const handleSearch = useCallback(
    (text: string) => {
      const formattedQuery = text.toLowerCase();
      if (formattedQuery !== "") {
        loadSearchResultsThrottled(formattedQuery);
      }
      setSearchTerm(formattedQuery);
    },
    [loadSearchResultsThrottled]
  );

  const itemsToShow: TrackingLinkData[] = useMemo(() => {
    let items: TrackingLinkData[] = fetchedLinkTrackers;
    if (searchTerm !== "") {
      items = filter(items, (item: TrackingLinkData) => {
        return contains(item, searchTerm);
      });
    }
    return items;
  }, [fetchedLinkTrackers, searchTerm]);

  const handleDropdownTrackingLinkMenuClose = useCallback(() => {
    setTrackingLinkModalText("");
    setLinkTrackerId(undefined);
  }, [setTrackingLinkModalText, setLinkTrackerId]);

  const copyTrackingLink = useCallback(
    (linkMapping: LinkMapping) => {
      if (linkMapping) {
        const link = `https://markitai.com/e/${event.id}?l=${linkMapping.id}`;

        navigator.clipboard
          .writeText(link)
          .then(() => {
            showNotificationBanner(
              dispatch,
              "Copied!",
              NotificationType.AFFIRMATIVE
            );
          })
          .catch((err) => {
            setAlertText(err);
          });
        handleDropdownTrackingLinkMenuClose();
      }
    },
    [dispatch, event.id, handleDropdownTrackingLinkMenuClose]
  );

  const editTrackingLink = useCallback(
    (item: EventLinkTracker) => {
      setTrackingLinkModalVisible(true);
      setTrackingLinkModalText(item.alias);
      setLinkTrackerId(item.id);
    },
    [setLinkTrackerId, setTrackingLinkModalText, setTrackingLinkModalVisible]
  );

  const deleteTrackingLink = useCallback(async () => {
    if (linkTrackerId) {
      await API.tracking
        .deleteEventLinkTracker({
          userId: event.createdBy,
          eventId: event.id,
          linkTrackerId: linkTrackerId,
        })
        .then(() => {
          handleDropdownTrackingLinkMenuClose();
        });
      showNotificationBanner(
        dispatch,
        "Tracking Link Deleted",
        NotificationType.NEGATIVE,
        "ion:trash"
      );
    }
  }, [
    dispatch,
    event.createdBy,
    event.id,
    handleDropdownTrackingLinkMenuClose,
    linkTrackerId,
  ]);

  const newTrackingLinkOnPress = useCallback(() => {
    setTrackingLinkModalVisible(true);
  }, [setTrackingLinkModalVisible]);

  const renderEmptyTrackingLinks = useMemo(
    () => (
      <EmptyStateFlatlist
        searchTerm={searchTerm}
        isLoading={isLoadingTrackingLinks}
        containerStyles={{ paddingTop: 120 }}
        nonSearchEmptyView={
          <EmptyStateButton
            title={"No Tracking Links"}
            btnText="New Tracking Link"
            description={
              showConversions
                ? "Gain insights into opens and conversions of your different promotion channels"
                : "Gain insights into opens of your different promotion channels"
            }
            icon={
              <Icon
                icon={"ion:analytics"}
                height={49}
                style={{ color: Colors.GRAY1 }}
              />
            }
            iconBox={73}
            onPress={newTrackingLinkOnPress}
            containerStyles={{ paddingTop: 120 }}
          />
        }
      />
    ),
    [
      isLoadingTrackingLinks,
      newTrackingLinkOnPress,
      searchTerm,
      showConversions,
    ]
  );

  return (
    <div className="RowNormal" style={{ gap: 30, paddingBottom: 40 }}>
      <div className="ColumnNormal" style={{ flex: 1 }}>
        {isProductWithCheckout(event.eventType, event.accessLevel) &&
        ticketAnalyticsData ? (
          <TicketAnalyticsSection
            event={event}
            userTicket={ticketAnalyticsData.userTicket}
            totalScanned={ticketAnalyticsData.totalScanned}
            totalGuests={totalGuests}
            totalRevenue={totalRevenue}
            totalTips={totalTips}
            totalLinkViews={totalLinkViews}
            dashboardVisibility={ticketAnalyticsData.dashboardVisibility}
            isPaymentCollectorRole={ticketAnalyticsData.isPaymentCollectorRole}
          />
        ) : (
          <EventDashboardLinkAnalyticsBox
            totalLinkViews={totalLinkViews}
            totalSubmissions={
              isProductSpecificType(event.eventType) ||
              isExternalLinkNoData(event.eventType)
                ? undefined
                : totalGuests
            }
          />
        )}
      </div>
      {canMassText ? (
        <div className="ColumnNormal" style={{ flex: 1 }}>
          <div style={styles.sectionHeaderText}>Tracking Links</div>
          <StandardBorderedContainer
            containerStyles={styles.overallContainerView}
          >
            <div className="ColumnNormal" style={{ minHeight: 770 }}>
              <div
                className="AlignedRow"
                style={{ ...styles.containerView, paddingBlock: 14, gap: 12 }}
              >
                <SearchBoxContainer
                  placeholder="Search Tracking Links..."
                  onChange={(e) => handleSearch(e.target.value)}
                  containerStyles={{ marginTop: 0 }}
                />
                <BlackHoverButton
                  title={"New Link"}
                  iconName="ion:add-circle"
                  iconSize={19}
                  onPress={newTrackingLinkOnPress}
                  permanentHover
                  containerStyles={{
                    paddingInline: 14,
                    paddingBlock: 12,
                    whiteSpace: "nowrap",
                  }}
                />
              </div>
              <HorizontalDivider />
              <GridTableHeader
                flexSections={[1, 1, 1]}
                sectionTitles={[
                  "Name",
                  "Opens",
                  showConversions ? "Conversions" : "",
                ]}
              />
              <HorizontalDivider />
              <div
                className="HideScrollbar"
                style={{
                  ...styles.containerView,
                  ...styles.trackingLinkView,
                  overflowY: "scroll",
                }}
              >
                <FlatList
                  list={itemsToShow}
                  scrollEnabled={false}
                  keyExtractor={(item: TrackingLinkData) =>
                    item.eventLinkTracker.id
                  }
                  renderItem={(item) => {
                    const itemTrackingOverview =
                      item.eventLinkTracker.trackingOverviews[
                        DefaultLinkTrackerAttachmentId
                      ];

                    return (
                      <TrackingLinkItem
                        item={item.eventLinkTracker}
                        itemTrackingOverview={itemTrackingOverview}
                        numTotalLinkTrackers={numTotalLinkTrackers}
                        showConversions={showConversions}
                        copyTrackingLink={() =>
                          copyTrackingLink(item.linkMapping)
                        }
                        editTrackingLink={() =>
                          editTrackingLink(item.eventLinkTracker)
                        }
                        deleteTrackingLink={() => {
                          setDeleteLinkConfirm(true);
                          setLinkTrackerId(item.eventLinkTracker.id);
                        }}
                      />
                    );
                  }}
                  renderWhenEmpty={() => renderEmptyTrackingLinks}
                  hasMoreItems={!isFinishedTrackingLinks}
                  loadMoreItems={() => {
                    if (searchTerm.trim() === "" && !isLoadingTrackingLinks) {
                      fetchMoreTrackingLinks();
                    }
                  }}
                  paginationLoadingIndicator={() =>
                    isLoadingTrackingLinks ? (
                      <div className="Centering">
                        <CircularProgress
                          style={{ color: Colors.GRAY1, marginBottom: 14 }}
                          size={24}
                        />
                      </div>
                    ) : null
                  }
                />
              </div>
            </div>
          </StandardBorderedContainer>
        </div>
      ) : null}
      <AlertContainer
        headerComp={alertText}
        theme={theme}
        closeModal={() => setAlertText("")}
        hideModal={alertText === ""}
      />
      <ConfirmDeleteModal
        heading="Are you sure you wish to delete this tracking link?"
        deleteButtonText="Delete"
        hideModal={!deleteLinkConfirm}
        setIsVisible={setDeleteLinkConfirm}
        deleteOnPress={deleteTrackingLink}
        theme={theme}
      />
    </div>
  );
};
